/**
 * AuthGuard - Protects routes and optionally redirects by subscription/MFA
 *
 * Purpose: Wrap protected content; redirect to /auth if not authenticated;
 * unpaid users on protected routes return to /auth (login first).
 * Dependencies: react-router-dom, @core/auth, hooks/billing
 * Exports: AuthGuard
 * Safety: 🟢 SAFE
 * RECENT: 2026-07-17 [2.2.1608] — Wait for post-login subscription fetch before paywall redirect (avoids stale null).
 * RECENT_PRIOR: 2026-06-13 [2.2.1383] — AuthGuard wired on protected routes; tenant-select and subscription-setup allowed without active subscription.
 */

import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { useAuth } from '@core/auth';

import { useSubscription } from '../../hooks/billing';

const PATHS_ALLOWED_WITHOUT_SUBSCRIPTION = [
  '/subscription-setup',
  '/tenant-select',
  '/mfa-setup',
  '/welcome',
  '/pricing',
  '/payment-confirm',
  '/account-setup',
  '/auth',
  '/forgot-password',
  '/reset-password',
];

interface AuthGuardProps {
  children: ReactNode;
  requireSubscription?: boolean;
  fallbackPath?: string;
}

export function AuthGuard({
  children,
  requireSubscription = true,
  fallbackPath = '/auth',
}: AuthGuardProps) {
  const location = useLocation();
  const { isAuthenticated, isLoading } = useAuth();
  const { data: subscription, isLoading: subLoading } = useSubscription();
  const path = location.pathname;

  if (isLoading || (isAuthenticated && requireSubscription && subLoading)) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to={fallbackPath} state={{ from: location }} replace />;
  }

  if (requireSubscription) {
    const allowedWithout = PATHS_ALLOWED_WITHOUT_SUBSCRIPTION.some(
      (p) => path === p || path.startsWith(p + '/')
    );
    if (!allowedWithout && (!subscription || subscription.status !== 'active')) {
      return <Navigate to="/auth" replace state={{ from: location }} />;
    }
  }

  return <>{children}</>;
}
